/**
 * How a Deep research turn's plan reads (boards S2 and S3), from its model (`planModel.ts`) and the
 * MiniMarvs reading it (`TurnLane`): how much is read, what each MiniMarv is opening, the header's
 * words and each group row's. Pure, so the web's plan block and the native engine's plan card
 * (`engineTurn.ts`) read one set of rules.
 */
import {
    durationText,
    findPlanItem,
    formatItem,
    itemsText,
    shortNoun,
    type PlanGroup,
    type PlanModel,
} from "./planModel";
import type { LaneLookup, TurnLane, TurnPhase } from "./turnModel";

/**
 * Items read so far, live: a done group counts its size, a running group the distinct items its MiniMarv has
 * finished, a waiting group none. The header, the plan's "N of M read", the bar and the rows all use this one
 * number. The server's own `read` (a group's, which arrives when it ends) is a floor, so the count never goes
 * back when it lands.
 */
export function liveRead(plan: PlanModel, lanes: TurnLane[] = []): number {
    const sum = plan.groups.reduce((total, group) => {
        const lane = lanes.find((candidate) => candidate.group === group.index);
        const live =
            group.status === "done"
                ? group.size
                : group.status === "reading" && lane
                  ? Math.min(readSoFar(plan, lane, true).length, group.size)
                  : 0;
        return total + Math.max(live, group.read);
    }, 0);
    return Math.min(Math.max(sum, plan.read), plan.total);
}

/** The plan item a lookup names, as the plan words it ("08 42 29 Sliding Automatic Entrances › 1.4"), else its own words. */
export function lookupName(plan: PlanModel, lookup: LaneLookup): string | null {
    const item = findPlanItem(plan, lookup.item);
    return item ? formatItem(item.label) : lookup.item ? formatItem(lookup.item) : null;
}

/** What a MiniMarv is doing now, in its words: "Opening 01 74 19 Construction Waste Management and Disposal › 1.5". */
export function currentAction(plan: PlanModel, lane: TurnLane | undefined): string | null {
    const current = lane?.lookups[lane.lookups.length - 1];
    if (!lane || !current) return null;
    const name = lookupName(plan, current);
    return name ? `Opening ${name}` : current.label;
}

/** The items a MiniMarv has finished opening, each once, in order (the one it is on now is not among them). */
export function readSoFar(plan: PlanModel, lane: TurnLane, reading: boolean): LaneLookup[] {
    const seen = new Set<string>();
    const kept = reading ? lane.opened.slice(0, -1) : lane.opened;
    return kept.filter((lookup) => {
        const key = lookupName(plan, lookup) ?? lookup.label;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
}

/** The items found in a read item: "…" until the MiniMarv has recorded them. */
export function foundText(plan: PlanModel, lookup: LaneLookup): string | null {
    const item = findPlanItem(plan, lookup.item);
    if (!item) return null;
    return item.status === "read" || item.found > 0 ? itemsText(item.found) : "… items";
}

/** What the finished work leaves visible under "Show all work" (board S3): what it built, and how much was read. */
export function deepTailRows(
    plan: PlanModel,
    built: string | null,
): Array<{ id: string; text: string; op: "write" | "check"; result: string }> {
    const noun = shortNoun(plan.noun);
    const rows: Array<{ id: string; text: string; op: "write" | "check"; result: string }> = [];
    if (built)
        rows.push({ id: "tail-built", text: built, op: "write", result: `${plan.total} ${noun}` });
    rows.push({
        id: "tail-checked",
        text:
            plan.read === plan.total
                ? `Checked every ${plan.nounOne.replace(/^specification\s+/i, "")}`
                : `Checked ${noun}`,
        op: "check",
        result: `${plan.read} of ${plan.total}`,
    });
    return rows;
}

/** "Built the spreadsheet" from the step that prepared it ("Preparing the spreadsheet for you"); null when there is none. */
export function builtText(label: string | undefined): string | null {
    const match = label
        ? /^(?:Preparing|Building|Creating|Writing|Making)\s+the\s+(\w+)/i.exec(label)
        : null;
    return match ? `Built the ${match[1].toLowerCase()}` : null;
}

/** "Deep research · 38 of 136 sections · 2 min 10 s" while it runs; "Worked 5 min 40 s · 4 MiniMarvs · 14 groups" after. */
export function deepHeaderText(
    plan: PlanModel,
    lanes: TurnLane[] | undefined,
    phase: TurnPhase,
    duration: number | null,
): { title: string; meta: string } {
    const read = liveRead(plan, lanes);
    const live = phase === "live";
    const noun = shortNoun(plan.noun);
    let title: string;
    let meta: string;
    if (live) {
        title = "Deep research";
        meta = [
            `${read} of ${plan.total} ${noun}`,
            duration === null ? null : durationText(duration),
        ]
            .filter(Boolean)
            .join(" · ");
    } else {
        const worked =
            phase === "complete" ? "Worked" : phase === "cancelled" ? "Stopped" : "Couldn't finish";
        title = duration === null ? worked : `${worked} ${durationText(duration)}`;
        meta =
            phase === "complete"
                ? [
                      plan.workers > 0 ? `${plan.workers} MiniMarvs` : null,
                      `${plan.groups.length} groups`,
                  ]
                      .filter(Boolean)
                      .join(" · ")
                : `${read} of ${plan.total} ${noun} read`;
    }
    return { title, meta };
}

/** A group row's right-hand words: "10 sections · 4 items" done, "reading · 3 of 10" while read, "waiting". */
export function groupMeta(plan: PlanModel, group: PlanGroup, lane: TurnLane | undefined): string {
    const noun = shortNoun(plan.noun);
    const reading = group.status === "reading";
    const readCount =
        lane && reading ? Math.min(readSoFar(plan, lane, true).length, group.size) : null;
    return group.status === "done"
        ? `${group.size} ${noun} · ${itemsText(group.found)}`
        : reading
          ? readCount === null
              ? "reading"
              : `reading · ${readCount} of ${group.size}`
          : "waiting";
}

/** The plan block's top line and bar: "Plan · 136 sections in 14 groups", "38 of 136 read", and its percent. */
export function planSummary(
    plan: PlanModel,
    lanes: TurnLane[] | undefined,
): { read: number; eyebrow: string; readText: string; percent: number } {
    const read = liveRead(plan, lanes);
    return {
        read,
        eyebrow: `Plan · ${plan.total} ${shortNoun(plan.noun)} in ${plan.groups.length} groups`,
        readText: `${read} of ${plan.total} read`,
        percent: plan.total > 0 ? Math.round((read / plan.total) * 100) : 0,
    };
}

/** A run of waiting groups as one row: "Groups 6 to 14 · 90 sections". */
export function waitingRunText(
    plan: PlanModel,
    row: { from: number; to: number; sections: number },
): string {
    return `Groups ${row.from} to ${row.to} · ${row.sections} ${shortNoun(plan.noun)}`;
}

/** A group row's words: "Group 5 · 07 54 19 to 08 42 29". */
export function groupText(group: PlanGroup): string {
    return `Group ${group.index} · ${group.label}`;
}

/** The chip before a group a MiniMarv has started: "MiniMarv 2"; none for a waiting group. */
export function workerText(group: PlanGroup): string | null {
    return group.worker !== undefined && group.status !== "waiting"
        ? `MiniMarv ${group.worker}`
        : null;
}
